import { useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { getExcelBlob } from '../components/dashboard/utils/excelGenerator';
import { ChartCaptureSizeError } from '../components/dashboard/utils/chartDownloader';
import { DashboardData, FileJobAcceptedResponse, FileJobStatusResponse } from '../types';
import { getApiErrorStatus, getUserFacingApiErrorMessage } from '../utils/apiErrorMessage';
import { DashboardFilters } from './useDashboardData';

type DownloadDashboardOptions = {
  includeReasonSheets: boolean;
  timeSeriesValueMode?: 'ratioOnly' | 'ratioAndCount';
  filters: DashboardFilters;
};

const EXPORT_SIZE_ERROR_MESSAGE =
  'データ出力サイズが上限を超えました。表示条件を絞って再試行してください。';
const EXPORT_FAILURE_MESSAGE =
  'データ出力に失敗しました。時間をおいて再試行してください。';

class DashboardExportFailure extends Error {
  constructor() {
    super('dashboard_export_failed');
    this.name = 'DashboardExportFailure';
  }
}

const getDownloadErrorMessage = (error: unknown): string => {
  if (error instanceof ChartCaptureSizeError) {
    return EXPORT_SIZE_ERROR_MESSAGE;
  }
  if (error instanceof DashboardExportFailure) {
    return EXPORT_FAILURE_MESSAGE;
  }
  if (getApiErrorStatus(error) === 413) {
    return EXPORT_SIZE_ERROR_MESSAGE;
  }
  return getUserFacingApiErrorMessage(error);
};

// downloadChartsの代わりに、画像取得関数を引数で受け取る
export const useDownloadDashboard = (
  fileId: string | undefined,
  dashboardData: DashboardData | undefined,
  getChartImages: () => Promise<{ name: string; blob: Blob }[]>,
  options: DownloadDashboardOptions,
  fileName?: string
) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!dashboardData || !fileId) return;

    const downloadBaseName = (fileName ?? fileId).replace(/\.[^/.]+$/, '') || 'dashboard';

    setDownloadError(null);
    setIsDownloading(true);
    try {
      let excelBlob: Blob | null;
      try {
        excelBlob = getExcelBlob(dashboardData, {
          includeReasonSheets: options.includeReasonSheets,
          includeExtendedTimeSeriesSheets: true,
          timeSeriesValueMode: options.timeSeriesValueMode ?? 'ratioAndCount',
        });
      } catch {
        throw new DashboardExportFailure();
      }
      if (!excelBlob) {
        throw new DashboardExportFailure();
      }

      let chartImages: { name: string; blob: Blob }[];
      try {
        // 外部から渡された関数でグラフ画像を取得
        chartImages = await getChartImages();
      } catch (error) {
        if (error instanceof ChartCaptureSizeError) {
          throw error;
        }
        throw new DashboardExportFailure();
      }
      if (chartImages.length > 32) throw new DashboardExportFailure();

      const formData = new FormData();
      formData.append(
        'workbook',
        excelBlob,
        '手指衛生_report_' + downloadBaseName + '.xlsx',
      );
      formData.append('filters', JSON.stringify(options.filters));
      formData.append('options', JSON.stringify({
        includeReasonSheets: options.includeReasonSheets,
        timeSeriesValueMode: options.timeSeriesValueMode ?? 'ratioAndCount',
      }));
      chartImages.forEach(({ name, blob }) => {
        formData.append('charts', blob, name);
      });
      const accepted = await axios.post<FileJobAcceptedResponse>(
        `${process.env.REACT_APP_API_URL}/api/files/dashboard/${fileId}/exports`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      let job: FileJobStatusResponse;
      for (;;) {
        const response = await axios.get<FileJobStatusResponse>(
          `${process.env.REACT_APP_API_URL}/api/files/jobs/${accepted.data.jobId}`,
        );
        job = response.data;
        if (job.status === 'completed') break;
        if (job.status === 'failed' || job.status === 'cancelled') {
          throw new DashboardExportFailure();
        }
        await new Promise(resolve => window.setTimeout(resolve, 1000));
      }
      const artifact = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/files/jobs/${job.jobId}/artifact`,
        { responseType: 'blob' },
      );
      saveAs(artifact.data, `手指衛生_report_${downloadBaseName}.zip`);
    } catch (err) {
      console.error('ダウンロード処理中にエラーが発生しました。', err);
      setDownloadError(getDownloadErrorMessage(err));
    } finally {
      setIsDownloading(false);
    }
  };

  const clearDownloadError = () => {
    setDownloadError(null);
  };

  return {
    isDownloading,
    downloadError,
    handleDownload,
    clearDownloadError,
  };
};
